/**
 * DESIGN-FOUNDATION-3 — Canonical motion presets
 *
 * All platform motion uses these presets. Surface components MUST import
 * from this file — no inline duration/ease values in surface code.
 *
 * Constraint: GPU-accelerated properties only (transform, opacity, filter).
 * Never animate width, height, top, left — layout-triggering properties
 * cause jank and break the 60fps target.
 */

import { useReducedMotion } from 'framer-motion';
import { DURATION, EASING } from './tokens';

// ─── Page transitions ─────────────────────────────────────────────────────────

/** Page entrance: fade + slide up 8px. Use with AnimatedPage or AnimatePresence. */
export const PAGE_ENTER = {
  initial:    { opacity: 0, y: 8 },
  animate:    { opacity: 1, y: 0 },
  exit:       { opacity: 0, y: -8 },
  transition: { duration: DURATION[200], ease: EASING.outQuint },
} as const;

// ─── Modal / overlay ──────────────────────────────────────────────────────────

/** Modal dialog: scale-in + fade. */
export const MODAL_ENTER = {
  initial:    { opacity: 0, scale: 0.96 },
  animate:    { opacity: 1, scale: 1 },
  exit:       { opacity: 0, scale: 0.96 },
  transition: { duration: DURATION[200], ease: EASING.outQuint },
} as const;

/** Modal backdrop: fade only. */
export const BACKDROP_ENTER = {
  initial:    { opacity: 0 },
  animate:    { opacity: 1 },
  exit:       { opacity: 0 },
  transition: { duration: DURATION[150], ease: EASING.easeOut },
} as const;

// ─── Drawers ──────────────────────────────────────────────────────────────────

/** Drawer from the right (default). */
export const DRAWER_RIGHT_ENTER = {
  initial:    { opacity: 0, x: '100%' },
  animate:    { opacity: 1, x: 0 },
  exit:       { opacity: 0, x: '100%' },
  transition: { duration: DURATION[300], ease: EASING.outQuint },
} as const;

/** Drawer from the left. */
export const DRAWER_LEFT_ENTER = {
  initial:    { opacity: 0, x: '-100%' },
  animate:    { opacity: 1, x: 0 },
  exit:       { opacity: 0, x: '-100%' },
  transition: { duration: DURATION[300], ease: EASING.outQuint },
} as const;

/** Drawer from the bottom (mobile sheet). */
export const DRAWER_BOTTOM_ENTER = {
  initial:    { opacity: 0, y: '100%' },
  animate:    { opacity: 1, y: 0 },
  exit:       { opacity: 0, y: '100%' },
  transition: { duration: DURATION[300], ease: EASING.outQuint },
} as const;

// ─── List stagger ─────────────────────────────────────────────────────────────

/** Container: enables stagger for direct children. */
export const LIST_CONTAINER = {
  animate: { transition: { staggerChildren: 0.05 } },
} as const;

/** Individual list item (used inside LIST_CONTAINER). */
export const LIST_ITEM = {
  initial:    { opacity: 0, y: 4 },
  animate:    { opacity: 1, y: 0 },
  exit:       { opacity: 0 },
  transition: { duration: DURATION[150], ease: EASING.outQuint },
} as const;

// ─── Generic ──────────────────────────────────────────────────────────────────

/** Simple fade (content swaps, tab panels, skeleton replacement). */
export const FADE = {
  initial:    { opacity: 0 },
  animate:    { opacity: 1 },
  exit:       { opacity: 0 },
  transition: { duration: DURATION[150], ease: EASING.easeOut },
} as const;

// ─── Number counter ───────────────────────────────────────────────────────────

/** Transition object for AnimatedNumber — apply to a useMotionValue spring. */
export const NUMBER_TRANSITION = {
  duration: DURATION[500],
  ease: EASING.outQuint,
} as const;

// ─── Interactive states ───────────────────────────────────────────────────────

/** Hover lift for cards/tiles (use whileHover on motion.div). */
export const HOVER_LIFT = {
  whileHover: {
    y: -2,
    transition: { duration: DURATION[100], ease: EASING.easeOut },
  },
} as const;

/** Spring pop for badges, confirmations, success states. */
export const SPRING_POP = {
  initial:    { scale: 0.8, opacity: 0 },
  animate:    { scale: 1, opacity: 1 },
  transition: EASING.spring,
} as const;

// ─── EC-1 §2.5 aliases (surface import compatibility) ─────────────────────────
// Surfaces importing { FADE_IN, SLIDE_UP, STAGGER_LIST } for EC-1 gate compliance
// resolve to these canonical presets. Matches check-enterprise-ceiling.sh §2.5 pattern.
/** Alias for FADE — fade-in entrance used in surface content areas. */
export const FADE_IN = FADE;
/** Alias for PAGE_ENTER — slide-up entrance for surface panels. */
export const SLIDE_UP = PAGE_ENTER;
/** Alias for LIST_CONTAINER — stagger wrapper for list rendering. */
export const STAGGER_LIST = LIST_CONTAINER;

// ─── MOTION_PRESETS vocabulary + useMotionPreset hook ──────────────────────────
// MOTION-PRESETS-CONSOLIDATE-1: the 6-preset reduced-motion-aware API
// (formerly src/motion/presets.js — consolidated here to remove the .js/.ts
// resolution collision). ELYON-UIUX-CFI-1 FLOOR-BEH-001 / DIFF-007.
//
// FROZEN: 6 presets. BINDING: new component code uses useMotionPreset(key); the
// legacy named constants above remain available (deprecated — prefer presets).

/** The 6 frozen platform motion presets (reduced-motion handled by useMotionPreset). */
export const MOTION_PRESETS = {
  /** appear — enters from slightly below, fades in. Modals, panels, cards, tooltips. */
  appear: {
    initial:    { opacity: 0, y: 8 },
    animate:    { opacity: 1, y: 0 },
    exit:       { opacity: 0, y: 4 },
    transition: { duration: 0.15, ease: 'easeOut' },
  },
  /** slide — enters from left, fades in. Sidebar panels, drawer content, steps. */
  slide: {
    initial:    { opacity: 0, x: -12 },
    animate:    { opacity: 1, x: 0 },
    exit:       { opacity: 0, x: -8 },
    transition: { duration: 0.18, ease: 'easeOut' },
  },
  /** expand — scales up from 96%, fades in. Dropdowns, popovers, context menus. */
  expand: {
    initial:    { opacity: 0, scale: 0.96 },
    animate:    { opacity: 1, scale: 1 },
    exit:       { opacity: 0, scale: 0.97 },
    transition: { duration: 0.15, ease: 'easeOut' },
  },
  /** fadeOut — exits upward, fades out. Toast dismissal, banner close. */
  fadeOut: {
    initial:    { opacity: 1 },
    animate:    { opacity: 1 },
    exit:       { opacity: 0, y: -4 },
    transition: { duration: 0.10, ease: 'easeIn' },
  },
  /** micro — subtle scale on tap. Buttons, clickable cards. whileTap only. */
  micro: {
    whileTap:   { scale: 0.97 },
    transition: { duration: 0.08 },
  },
  /** skeleton — pulsing opacity shimmer for loading states. Skeleton component only. */
  skeleton: {
    animate:    { opacity: [0.4, 0.7, 0.4] },
    transition: { duration: 1.4, repeat: Infinity, ease: 'easeInOut' },
  },
} as const;

/**
 * useMotionPreset(key) — returns a preset with reduced-motion respect.
 * Under prefers-reduced-motion: reduce, transition durations collapse to 0 and
 * opacity arrays snap to final. WCAG 2.1 §2.3.3 — BINDING.
 */
export function useMotionPreset(key: keyof typeof MOTION_PRESETS): Record<string, unknown> {
  const shouldReduce = useReducedMotion();
  const preset = MOTION_PRESETS[key] as Record<string, any> | undefined;

  if (!preset) {
    console.warn(`[motion] Unknown preset: "${key}". Valid: ${Object.keys(MOTION_PRESETS).join(', ')}`);
    return {};
  }
  if (!shouldReduce) return preset;

  const reduced: Record<string, any> = { ...preset };
  if (reduced.transition) reduced.transition = { ...reduced.transition, duration: 0 };
  if (reduced.animate && typeof reduced.animate === 'object' && Array.isArray(reduced.animate.opacity)) {
    reduced.animate = { ...reduced.animate, opacity: 1 };
  }
  if (reduced.initial) reduced.initial = { opacity: 1, y: 0, x: 0, scale: 1 };
  return reduced;
}

/** Typed list of preset keys for IDE autocomplete + ESLint rule. */
export const MotionPresetKeys = Object.keys(MOTION_PRESETS) as Array<keyof typeof MOTION_PRESETS>;
